import { useMemo, useState } from 'react';
import {
    Box, Flex, Grid, Heading, Text, Image, Input, InputGroup,
    InputLeftElement, IconButton, Button, Skeleton, SkeletonText,
    SkeletonCircle, Badge,
} from '@chakra-ui/react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { MdVerified } from 'react-icons/md';
import ProductItemNew from 'components/Dashboard/ProductItemNew';
import ApiPagination from 'components/Dashboard/ApiPagination';
import { ITEMS_PER_PAGE } from 'utils/utils';
import { useCurrency } from 'hooks/useCurrency';
import {
    useVendorById,
    normalizeVendor,
} from 'hooks/useVendors';
import { useVendorProducts } from 'hooks/useVendorProducts';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';
const INFO        = '#2563EB';

const FONT      = "'Bricolage Grotesque', sans-serif";
const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

const PER_PAGE  = ITEMS_PER_PAGE ?? 12;

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const SearchIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const CloseIcon = (props) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);

const BackIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const StoreIcon = (props) => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M3 9l1.5-5h15L21 9 M3 9v11h18V9 M3 9h18 M8 14h8" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
);

const PinIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M12 22s-7-7-7-12a7 7 0 1114 0c0 5-7 12-7 12z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   PRODUCT GRID SKELETON
   ───────────────────────────────────────────────────────────── */
const ProductGridSkeleton = ({ count = PER_PAGE }) => (
    <Grid
        templateColumns={{
            base: 'repeat(2, 1fr)',
            sm:   'repeat(2, 1fr)',
            md:   'repeat(3, 1fr)',
            lg:   'repeat(4, 1fr)',
            xl:   'repeat(5, 1fr)',
        }}
        gap={{ base: '10px', md: '14px' }}
        w="full"
        minW="0"
    >
        {Array.from({ length: count }).map((_, i) => (
            <Box
                key={i}
                bg={SURFACE}
                borderRadius="10px"
                overflow="hidden"
                border="1px solid"
                borderColor={BORDER}
            >
                <Skeleton h="160px" w="full" startColor={SURFACE_ALT} endColor={BORDER} />
                <Box p="10px">
                    <Skeleton h="11px" w="80%" mb="6px" startColor={SURFACE_ALT} endColor={BORDER} />
                    <Skeleton h="11px" w="50%" startColor={SURFACE_ALT} endColor={BORDER} />
                </Box>
            </Box>
        ))}
    </Grid>
);

/* ─────────────────────────────────────────────────────────────
   VENDOR HERO — name, image, verified, address, MOQ
   Renders as soon as vendor detail is loaded, independent of products
   ───────────────────────────────────────────────────────────── */
const VendorHero = ({ vendor, searchTerm, onSearchChange }) => {
    /* MOQ currency conversion */
    const moqRaw  = Number(vendor?.moq ?? 0);
    const moqConv = useCurrency(moqRaw);
    const moqSym  = moqConv?.symbol ?? '₦';
    const moqVal  = Number(moqConv?.price ?? moqRaw);

    return (
        <Box
            bg={SURFACE}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="14px"
            boxShadow={SHADOW_SM}
            p={{ base: '14px', md: '20px' }}
            mb="18px"
            minW="0"
        >
            <Flex
                direction={{ base: 'column', md: 'row' }}
                gap={{ base: '14px', md: '20px' }}
                align={{ base: 'stretch', md: 'flex-start' }}
                justify="space-between"
                minW="0"
            >
                {/* LEFT — image + identity */}
                <Flex gap="14px" minW="0" flex="1" align="flex-start">
                    <Box
                        w={{ base: '60px', md: '80px' }}
                        h={{ base: '60px', md: '80px' }}
                        borderRadius="12px"
                        overflow="hidden"
                        bg={CYAN}
                        flexShrink={0}
                        border="1px solid"
                        borderColor={BORDER}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        color={NAVY}
                    >
                        {vendor?.image ? (
                            <Image
                                src={vendor.image}
                                alt={vendor.name}
                                w="full"
                                h="full"
                                objectFit="cover"
                            />
                        ) : (
                            <StoreIcon />
                        )}
                    </Box>

                    <Box minW="0" flex="1">
                        <Flex align="center" gap="6px" mb="4px" flexWrap="wrap">
                            <Heading
                                as="h1"
                                fontSize={{ base: '20px', md: '26px' }}
                                fontWeight="800"
                                color={INK}
                                fontFamily={FONT}
                                letterSpacing="-0.02em"
                                lineHeight="1.2"
                                noOfLines={2}
                                title={vendor?.name}
                            >
                                {vendor?.name}
                            </Heading>
                            {vendor?.verified && (
                                <Box color={INFO} display="flex" title="Verified vendor" mt="2px">
                                    <MdVerified size={20} />
                                </Box>
                            )}
                        </Flex>

                        {vendor?.verified && (
                            <Badge
                                bg="#EFF6FF"
                                color={INFO}
                                fontSize="9px"
                                fontWeight="800"
                                px="6px"
                                py="1px"
                                borderRadius="full"
                                border={`1px solid ${INFO}30`}
                                mb="8px"
                                letterSpacing="0.04em"
                            >
                                VERIFIED VENDOR
                            </Badge>
                        )}

                        {vendor?.description && (
                            <Text fontSize="12px" color={MUTED} lineHeight="1.5" mb="6px" noOfLines={3}>
                                {vendor.description}
                            </Text>
                        )}

                        {vendor?.address && (
                            <Flex align="center" gap="4px" color={MUTED}>
                                <PinIcon />
                                <Text fontSize="11px" lineHeight="1.4" noOfLines={2}>
                                    {vendor.address}
                                </Text>
                            </Flex>
                        )}
                    </Box>
                </Flex>

                {/* RIGHT — MOQ + search */}
                <Box
                    flexShrink={0}
                    w={{ base: 'full', md: 'auto' }}
                    minW={{ md: '260px' }}
                    maxW={{ md: '320px' }}
                >
                    {moqRaw > 0 && (
                        <Box
                            bg={SURFACE_ALT}
                            border="1px solid"
                            borderColor={BORDER}
                            borderRadius="10px"
                            p="10px 12px"
                            mb="10px"
                            textAlign={{ base: 'left', md: 'right' }}
                        >
                            <Text
                                fontSize="9.5px"
                                color={MUTED}
                                fontWeight="700"
                                letterSpacing="0.06em"
                                mb="2px"
                            >
                                MINIMUM ORDER VALUE
                            </Text>
                            <Text
                                fontSize="18px"
                                fontWeight="800"
                                color={NAVY}
                                fontFamily={FONT}
                                lineHeight="1.1"
                                title={`${moqSym} ${moqVal.toLocaleString()}`}
                            >
                                {moqSym} {moqVal.toLocaleString()}
                            </Text>
                        </Box>
                    )}

                    <InputGroup>
                        <InputLeftElement pointerEvents="none" h="36px">
                            <Box color={FAINT}>
                                <SearchIcon />
                            </Box>
                        </InputLeftElement>
                        <Input
                            h="36px"
                            placeholder={`Search ${vendor?.name ?? 'products'}`}
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            bg={SURFACE}
                            border="1px solid"
                            borderColor={BORDER}
                            fontSize="12px"
                            fontFamily={FONT}
                            borderRadius="8px"
                            _focus={{ borderColor: NAVY, boxShadow: `0 0 0 1px ${NAVY}` }}
                            _placeholder={{ color: FAINT, fontSize: '12px' }}
                        />
                        {searchTerm && (
                            <IconButton
                                aria-label="Clear search"
                                icon={<CloseIcon />}
                                onClick={() => onSearchChange('')}
                                size="xs"
                                position="absolute"
                                right="6px"
                                top="50%"
                                transform="translateY(-50%)"
                                bg="transparent"
                                color={FAINT}
                                _hover={{ bg: SURFACE_ALT, color: INK }}
                                zIndex={2}
                            />
                        )}
                    </InputGroup>
                </Box>
            </Flex>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────
   HERO SKELETON — shown while vendor details load
   ───────────────────────────────────────────────────────────── */
const VendorHeroSkeleton = () => (
    <Box
        bg={SURFACE}
        border="1px solid"
        borderColor={BORDER}
        borderRadius="14px"
        boxShadow={SHADOW_SM}
        p={{ base: '14px', md: '20px' }}
        mb="18px"
    >
        <Flex direction={{ base: 'column', md: 'row' }} gap="20px">
            <Flex gap="14px" flex="1">
                <SkeletonCircle size={{ base: '60px', md: '80px' }} startColor={SURFACE_ALT} endColor={BORDER} />
                <Box flex="1">
                    <Skeleton h="22px" w="60%" mb="8px" startColor={SURFACE_ALT} endColor={BORDER} />
                    <Skeleton h="14px" w="30%" mb="10px" startColor={SURFACE_ALT} endColor={BORDER} />
                    <SkeletonText noOfLines={2} spacing="6px" skeletonHeight="9px" startColor={SURFACE_ALT} endColor={BORDER} />
                </Box>
            </Flex>
            <Box minW={{ md: '260px' }} flexShrink={0}>
                <Skeleton h="64px" w="full" mb="10px" borderRadius="10px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton h="36px" w="full" borderRadius="8px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Box>
        </Flex>
    </Box>
);

/* ─────────────────────────────────────────────────────────────
   SINGLE VENDOR — main page
   ───────────────────────────────────────────────────────────── */
const SingleVendor = () => {
    const { id: vendorId } = useParams();
    const navigate = useNavigate();

    /* Local UI state */
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    /* Vendor detail fetch — fires immediately on mount */
    const {
        vendor: rawVendor,
        isLoading: vendorLoading,
        error: vendorError,
    } = useVendorById(vendorId);
    console.log('[SV] rawVendor:', rawVendor, 'loading:', vendorLoading, 'error:', vendorError);


    /* Normalize once for the render cycle */
    const vendor = useMemo(
        () => (rawVendor ? normalizeVendor(rawVendor) : null),
        [rawVendor]
    );
        console.log('[SV] normalized vendor:', vendor);
    /* Products fetch — waits for vendor name to resolve before firing
       (useVendorProducts skips the network call when vendorName is falsy) */
    const {
        products,
        isLoading: productsLoading,
        hasMore,
        totalCount,
    } = useVendorProducts({
        vendorName: vendor?.name,
        page,
        per_page: PER_PAGE,
        search: searchTerm,
        debounce: 400,
    });
    console.log('[SV] vendorName sent to useVendorProducts:', vendor?.name);
    console.log('[SV] products:', products);

    /* Reset to page 1 whenever search changes */
    const handleSearchChange = (val) => {
        setSearchTerm(val);
        if (page !== 1) setPage(1);
    };

    /* ── Error state — vendor failed to load ──────────────── */
    if (vendorError && !vendor) {
        return (
            <Box w="full" px={{ base: '12px', md: '20px' }} py="40px" textAlign="center">
                <Heading fontSize="18px" color={INK} mb="6px" fontFamily={FONT}>
                    Vendor not found
                </Heading>
                <Text fontSize="13px" color={MUTED} mb="14px">
                    We couldn&apos;t load this vendor. They may have been removed or the link is invalid.
                </Text>
                <Button
                    leftIcon={<BackIcon />}
                    size="sm"
                    bg={NAVY}
                    color="white"
                    _hover={{ bg: NAVY_DEEP }}
                    fontFamily={FONT}
                    fontWeight="700"
                    fontSize="12px"
                    borderRadius="8px"
                    onClick={() => navigate('/dashboard/vendors')}
                >
                    Back to vendors
                </Button>
            </Box>
        );
    }

    return (
        <Box
            w="full"
            maxW="100%"
            minW="0"
            overflowX="hidden"
            px={{ base: '12px', md: '20px' }}
            py="20px"
            fontFamily={FONT}
        >
            {/* Breadcrumb / back link */}
            <Flex align="center" gap="6px" mb="14px" color={MUTED} fontSize="12px">
                <Button
                    as={RouterLink}
                    to="/dashboard/vendors"
                    variant="link"
                    color={MUTED}
                    fontWeight="600"
                    fontSize="12px"
                    leftIcon={<BackIcon />}
                    _hover={{ color: NAVY, textDecoration: 'none' }}
                    minW="auto"
                    h="auto"
                >
                    All vendors
                </Button>
                {vendor?.name && (
                    <>
                        <Text color={FAINT}>/</Text>
                        <Text color={INK} fontWeight="700" noOfLines={1}>
                            {vendor.name}
                        </Text>
                    </>
                )}
            </Flex>

            {/* Hero — skeleton or real */}
            {vendorLoading && !vendor ? (
                <VendorHeroSkeleton />
            ) : vendor ? (
                <VendorHero
                    vendor={vendor}
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                />
            ) : null}

            {/* Products section header */}
            <Flex
                justify="space-between"
                align="baseline"
                mb="12px"
                gap="10px"
                minW="0"
                flexWrap="wrap"
            >
                <Heading
                    as="h2"
                    fontSize={{ base: '14px', md: '16px' }}
                    fontWeight="800"
                    color={INK}
                    fontFamily={FONT}
                    letterSpacing="-0.01em"
                >
                    Products
                </Heading>
                {totalCount > 0 && (
                    <Text fontSize="11px" color={MUTED} fontWeight="600">
                        {totalCount} item{totalCount === 1 ? '' : 's'}
                    </Text>
                )}
            </Flex>

            {/* Products grid */}
            {productsLoading && products.length === 0 ? (
                <ProductGridSkeleton />
            ) : products.length === 0 ? (
                <Box
                    bg={SURFACE}
                    border="1px solid"
                    borderColor={BORDER}
                    borderRadius="12px"
                    p="40px 20px"
                    textAlign="center"
                >
                    <Text fontSize="13px" color={MUTED} mb="4px" fontWeight="700">
                        {searchTerm ? `No products match "${searchTerm}"` : 'No products yet'}
                    </Text>
                    <Text fontSize="11px" color={FAINT}>
                        {searchTerm
                            ? 'Try a different search term'
                            : 'This vendor hasn\'t listed any products.'}
                    </Text>
                    {searchTerm && (
                        <Button
                            mt="10px"
                            size="xs"
                            variant="link"
                            color={NAVY}
                            fontWeight="700"
                            onClick={() => handleSearchChange('')}
                        >
                            Clear search
                        </Button>
                    )}
                </Box>
            ) : (
                <>
                    {/* Subtle indicator when background refetch is happening */}
                    {productsLoading && products.length > 0 && (
                        <Flex justify="center" mb="8px">
                            <Text fontSize="10px" color={FAINT} fontWeight="700" letterSpacing="0.04em">
                                UPDATING…
                            </Text>
                        </Flex>
                    )}

                    <Grid
                        templateColumns={{
                            base: 'repeat(2, 1fr)',
                            sm:   'repeat(2, 1fr)',
                            md:   'repeat(3, 1fr)',
                            lg:   'repeat(4, 1fr)',
                            xl:   'repeat(5, 1fr)',
                        }}
                        gap={{ base: '10px', md: '14px' }}
                        w="full"
                        minW="0"
                    >
                        {products.map((item) => (
                            <Box
                                key={item.item_id ?? item.id}
                                minW="0"
                                w="full"
                                display="flex"
                                flexDirection="column"
                                h="full"
                                sx={{
                                    '& > *': {
                                        flex: 1,
                                        height: '100%',
                                        marginBottom: '0 !important',
                                    },
                                }}
                            >
                                <ProductItemNew item={item} />
                            </Box>
                        ))}
                    </Grid>

                    {/* Pagination — only show if backend reports more pages */}
                    {(hasMore || page > 1) && (
                        <Box mt="20px" display="flex" justifyContent="center">
                            <ApiPagination
                                currentPage={page}
                                hasMore={hasMore}
                                onPageChange={setPage}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default SingleVendor;